import React from 'react';
import { hasDriverRole } from '../utils/roleUtils';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Checkbox,
  Chip,
  Typography,
  Box,
  TablePagination,
  LinearProgress,
  Skeleton,
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Tooltip,
} from '@mui/material';
import {
  BiDotsVerticalRounded,
  BiPowerOff,
  BiRevision,
  BiLockOpen,
} from 'react-icons/bi';
import { toast } from 'react-toastify';
import { Users } from '@gql/base-types';
import { Link } from 'react-router-dom';
import UserCard from './UserCard';
import RoleChip from './RoleChip';
import { authService } from '@features/auth/services/authService';
import { useRoleManagement } from '../hooks/useRoleManagement';
import { canDeactivateUser } from '../utils/roleUtils';
import { useUser } from '@store';

// Unified feature chip color (same blue as Dealer Admin role)
const DEFAULT_FEATURE_CHIP_COLOR = { bg: '#eef4fb', text: '#4068a0' };  // secondaryAccent

type SortKey = 'id' | 'name' | 'role' | 'status';
type SortOrder = 'asc' | 'desc';

interface TeamTableProps {
  users: Users[];
  loading?: boolean;
  selectedIds: Set<number>;
  onSelectionChange: (ids: Set<number>) => void;
  refetch?: () => void;
}

const TeamTable: React.FC<TeamTableProps> = ({ users, loading, selectedIds, onSelectionChange, refetch }) => {
  const currentUser = useUser();
  const { deactivateUser, reactivateUser } = useRoleManagement();

  const [orderBy, setOrderBy] = React.useState<SortKey>('name');
  const [order, setOrder] = React.useState<SortOrder>('asc');
  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(25);

  // Action menu state
  const [menuAnchor, setMenuAnchor] = React.useState<null | HTMLElement>(null);
  const [menuUser, setMenuUser] = React.useState<Users | null>(null);

  const handleSort = (column: SortKey) => {
    const isAsc = orderBy === column && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(column);
  };

  const sortedUsers = React.useMemo(() => {
    return [...users].sort((a, b) => {
      let aVal: string | number | boolean = '';
      let bVal: string | number | boolean = '';

      switch (orderBy) {
        case 'id':
          aVal = a.id;
          bVal = b.id;
          break;
        case 'name':
          aVal = a.display_name?.toLowerCase() || '';
          bVal = b.display_name?.toLowerCase() || '';
          break;
        case 'role':
          aVal = a.default_role?.toLowerCase() || '';
          bVal = b.default_role?.toLowerCase() || '';
          break;
        case 'status':
          aVal = a.active ?? false;
          bVal = b.active ?? false;
          break;
      }

      if (aVal < bVal) return order === 'asc' ? -1 : 1;
      if (aVal > bVal) return order === 'asc' ? 1 : -1;
      return 0;
    });
  }, [users, orderBy, order]);

  const paginatedUsers = sortedUsers.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  const handleSelectAll = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked) {
      onSelectionChange(new Set(users.map(u => u.id)));
    } else {
      onSelectionChange(new Set());
    }
  };

  const handleSelectOne = (userId: number) => {
    const newSelected = new Set(selectedIds);
    if (newSelected.has(userId)) {
      newSelected.delete(userId);
    } else {
      newSelected.add(userId);
    }
    onSelectionChange(newSelected);
  };

  const getFeatures = (user: Users): { name: string; label: string }[] => {
    return ((user as any).users_usertofeatures || []).map((utf: any) => ({
      name: utf.feature.name,
      label: utf.feature.label,
    }));
  };

  const getRooftopCount = (user: Users): number => {
    return (user.users_usertocustomers || []).length;
  };

  const openMenu = (e: React.MouseEvent<HTMLElement>, user: Users) => {
    e.stopPropagation();
    e.preventDefault();
    setMenuAnchor(e.currentTarget);
    setMenuUser(user);
  };

  const closeMenu = () => {
    setMenuAnchor(null);
    setMenuUser(null);
  };

  const handleResetPassword = async () => {
    if (!menuUser?.email) return;
    closeMenu();
    try {
      await authService.resetPassword(menuUser.email);
      toast.success(`Password reset email sent to ${menuUser.email}`);
    } catch (err: any) {
      toast.error(`Failed to send password reset: ${err.message}`);
    }
  };

  const isSelf = menuUser?.id === currentUser?.profile?.id;
  const canDeactivate = menuUser
    ? !isSelf && canDeactivateUser(users, menuUser.id)
    : false;

  const handleDeactivate = async () => {
    if (!menuUser) return;
    closeMenu();
    const confirmed = window.confirm(
      `Deactivate ${menuUser.display_name || 'this user'}? They will lose access to all HopDrive systems.`
    );
    if (!confirmed) return;
    try {
      await deactivateUser(menuUser.id);
      toast.success('User deactivated');
      refetch?.();
    } catch (err: any) {
      toast.error(`Failed to deactivate: ${err.message}`);
    }
  };

  const handleReactivate = async () => {
    if (!menuUser) return;
    closeMenu();
    const confirmed = window.confirm(
      `Reactivate ${menuUser.display_name || 'this user'}? Their access will be restored.`
    );
    if (!confirmed) return;
    try {
      await reactivateUser(menuUser.id);
      toast.success('User reactivated');
      refetch?.();
    } catch (err: any) {
      toast.error(`Failed to reactivate: ${err.message}`);
    }
  };

  if (loading) {
    return (
      <TableContainer>
        <LinearProgress />
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell padding="checkbox"><Checkbox disabled /></TableCell>
              <TableCell>ID</TableCell>
              <TableCell>User</TableCell>
              <TableCell>Role</TableCell>
              <TableCell>Rooftops</TableCell>
              <TableCell>Features</TableCell>
              <TableCell>Status</TableCell>
              <TableCell sx={{ width: 48 }} />
            </TableRow>
          </TableHead>
          <TableBody>
            {[...Array(5)].map((_, i) => (
              <TableRow key={i}>
                <TableCell padding="checkbox"><Skeleton variant="rectangular" width={20} height={20} /></TableCell>
                <TableCell><Skeleton width={40} /></TableCell>
                <TableCell><Skeleton width={180} /></TableCell>
                <TableCell><Skeleton width={60} /></TableCell>
                <TableCell><Skeleton width={30} /></TableCell>
                <TableCell><Skeleton width={150} /></TableCell>
                <TableCell><Skeleton width={60} /></TableCell>
                <TableCell />
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    );
  }

  return (
    <>
      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell padding="checkbox">
                <Checkbox
                  indeterminate={selectedIds.size > 0 && selectedIds.size < users.length}
                  checked={users.length > 0 && selectedIds.size === users.length}
                  onChange={handleSelectAll}
                  size="small"
                />
              </TableCell>
              <TableCell sx={{ width: 60 }}>
                <TableSortLabel active={orderBy === 'id'} direction={orderBy === 'id' ? order : 'asc'} onClick={() => handleSort('id')}>
                  ID
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel active={orderBy === 'name'} direction={orderBy === 'name' ? order : 'asc'} onClick={() => handleSort('name')}>
                  User
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel active={orderBy === 'role'} direction={orderBy === 'role' ? order : 'asc'} onClick={() => handleSort('role')}>
                  Role
                </TableSortLabel>
              </TableCell>
              <TableCell>Rooftops</TableCell>
              <TableCell>Features</TableCell>
              <TableCell>
                <TableSortLabel active={orderBy === 'status'} direction={orderBy === 'status' ? order : 'asc'} onClick={() => handleSort('status')}>
                  Status
                </TableSortLabel>
              </TableCell>
              <TableCell sx={{ width: 48 }} />
            </TableRow>
          </TableHead>
          <TableBody>
            {paginatedUsers.map(user => {
              const isSelected = selectedIds.has(user.id);
              const features = getFeatures(user);
              const rooftopCount = getRooftopCount(user);
              const maxChips = 3;
              const visibleFeatures = features.slice(0, maxChips);
              const overflowCount = features.length - maxChips;

              return (
                <TableRow
                  key={user.id}
                  hover
                  selected={isSelected}
                  component={Link}
                  to={`/users/${user.id}`}
                  sx={{
                    textDecoration: 'none',
                    color: 'inherit',
                    cursor: 'pointer',
                    height: 48,
                    '&:hover': { backgroundColor: 'rgba(32, 36, 48, 0.02)' },
                    transition: 'background-color 150ms',
                  }}
                >
                  <TableCell padding="checkbox" onClick={e => { e.stopPropagation(); e.preventDefault(); }}>
                    <Checkbox
                      checked={isSelected}
                      onChange={() => handleSelectOne(user.id)}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="text.secondary">{user.id}</Typography>
                  </TableCell>
                  <TableCell>
                    <UserCard
                      compact
                      avatarUrl={user.avatar_url}
                      displayName={user.display_name || '—'}
                      email={user.email || '—'}
                      isActive={user.active}
                      isDriver={hasDriverRole((user as any).users_usertoroles)}
                      driverStatus={user.driver?.status ?? undefined}
                    />
                  </TableCell>
                  <TableCell>
                    <RoleChip role={user.default_role} />
                  </TableCell>
                  <TableCell>
                    <Chip label={rooftopCount} size="small" variant="outlined" />
                  </TableCell>
                  <TableCell>
                    <Box display="flex" gap={0.5} flexWrap="wrap">
                      {visibleFeatures.map(f => {
                        const colors = DEFAULT_FEATURE_CHIP_COLOR;
                        return (
                          <Chip
                            key={f.name}
                            label={f.label}
                            size="small"
                            sx={{ bgcolor: colors.bg, color: colors.text, fontSize: 11 }}
                          />
                        );
                      })}
                      {overflowCount > 0 && (
                        <Chip
                          label={`+${overflowCount}`}
                          size="small"
                          variant="outlined"
                          sx={{ fontSize: 11 }}
                        />
                      )}
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={user.active ? 'Active' : 'Inactive'}
                      size="small"
                      sx={{
                        bgcolor: user.active ? '#f3fbf4' : '#f1f2f3',
                        color: user.active ? '#30b448' : '#5f666d',
                        fontSize: 11,
                      }}
                    />
                  </TableCell>
                  <TableCell onClick={e => { e.stopPropagation(); e.preventDefault(); }}>
                    <Tooltip title="Actions">
                      <IconButton size="small" onClick={e => openMenu(e, user)}>
                        <BiDotsVerticalRounded size={18} />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              );
            })}

            {paginatedUsers.length === 0 && (
              <TableRow>
                <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                  <Typography variant="body2" color="text.secondary">
                    No users match your filters
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Row action menu */}
      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={closeMenu}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <MenuItem onClick={handleResetPassword} disabled={!menuUser?.email}>
          <ListItemIcon><BiLockOpen size={18} /></ListItemIcon>
          <ListItemText>Send reset password email</ListItemText>
        </MenuItem>
        {menuUser?.active ? (
          <MenuItem onClick={handleDeactivate} disabled={!canDeactivate} sx={{ color: canDeactivate ? 'error.main' : undefined }}>
            <ListItemIcon sx={{ color: canDeactivate ? 'error.main' : undefined }}><BiPowerOff size={18} /></ListItemIcon>
            <ListItemText>Deactivate</ListItemText>
          </MenuItem>
        ) : (
          <MenuItem onClick={handleReactivate} sx={{ color: 'success.main' }}>
            <ListItemIcon sx={{ color: 'success.main' }}><BiRevision size={18} /></ListItemIcon>
            <ListItemText>Reactivate</ListItemText>
          </MenuItem>
        )}
      </Menu>

      {users.length > 10 && (
        <TablePagination
          component="div"
          count={users.length}
          page={page}
          onPageChange={(_, newPage) => setPage(newPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={e => {
            setRowsPerPage(parseInt(e.target.value, 10));
            setPage(0);
          }}
          rowsPerPageOptions={[10, 25, 50]}
        />
      )}
    </>
  );
};

export default TeamTable;
